import labels from "../../utils/labels";
import StandardButton from "../UI/buttons/StandardButton";
import LabeledSection from "../UI/LabeledSection";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  EmployeeTransport,
  EmployeeWorkTime,
  TransportationType,
} from "../../types/ganttChart";
import { MutableRefObject, useEffect, useMemo, useRef, useState } from "react";
import useDataSelector from "../../hooks/useDataSelector";
import EmployeeTransportListItem from "./EmployeeTransportListItem";
import timestampService from "../../service/timestampService";
import EmployeeSettingsService from "../../service/employeeSettingsService";
import { editEmployeeTransportType } from "../../store/employeeSettings";
import { getGanttChartData } from "../../store/ganttChart/action";
import { useQueryParams } from "../../hooks/useQueryParams";
import TransportValidationUtil from "../../utils/transportValidationUtil";
import drawerStles from "../../styles/drawer.module.css";
const EmployeeTransportType = ({
  onUnsavedEdit,
  saveRef,
}: {
  onUnsavedEdit: (hasChanges: boolean) => void;
  saveRef?: MutableRefObject<(() => Promise<void>) | null>;
}) => {
  const { employeeShiftId } = useAppSelector(
    (state) => state.drawerSlice.selectedEmployee!,
  );
  const { dataToUse } = useDataSelector();
  const employee = dataToUse?.employees.find(
    (e) => e.shiftSchedule.idShift === employeeShiftId,
  );
  const transportationType = employee?.transportationType ?? [];
  const employeeWorkTime = useMemo<EmployeeWorkTime>(
    () => ({
      workTimeStart: employee?.shiftStartTime ?? null,
      workTimeEnd: employee?.shiftEndTime ?? null,
    }),
    [employee?.shiftStartTime, employee?.shiftEndTime],
  );
  const manualShiftChanges = employee?.manualShiftChanges;
  const [localTransports, setLocalTransports] = useState<EmployeeTransport[]>(
    [],
  );
  const initialTransportRef = useRef<EmployeeTransport[]>([]);

  const dispatch = useAppDispatch();
  const [isPending, setIsPending] = useState(false);
  const [isAddBlocked, setIsAddBlocked] = useState(false);
  const { manualEdits } = useAppSelector(
    (state) => state.employeeSettingsSlice,
  );
  const queryKey = useQueryParams();

  const manualTransportSegments =
    manualEdits[employeeShiftId]?.transportationType ??
    manualShiftChanges?.transportationType;

  const segmentsToUse = manualTransportSegments ?? transportationType;

  useEffect(() => {
    if (!segmentsToUse?.length) return;

    // Wire/store carries ISO-8601; the drawer working copy uses "HH:MM" so
    // TimePicker can edit it without timezone drift. We convert back at save.
    const mappedTransports: EmployeeTransport[] = segmentsToUse.map(
      (segment) => ({
        transportationType: segment.transportationType,
        start: timestampService.getHoursAndMinutesTimestamp(segment.start),
        end: timestampService.getHoursAndMinutesTimestamp(segment.end),
      }),
    );
    setLocalTransports(mappedTransports);
    initialTransportRef.current = mappedTransports;
  }, [segmentsToUse]);

  const getTransportTitle = (type: TransportationType) => {
    switch (type) {
      case "CAR":
        return labels.transportCar;
      case "BIKE":
        return labels.transportBike;
      case "ELECTRIC_BIKE":
        return labels.transportElectricBike;
      case "WALK":
        return labels.transportWalking;
    }
  };
  const transportOptions: TransportationType[] = [
    "CAR",
    "BIKE",
    "ELECTRIC_BIKE",
    "WALK",
  ];
  const options = useMemo(() => {
    return transportOptions.map((type) => ({
      id: type,
      title: getTransportTitle(type),
    }));
  }, []);

  const hasMultipleTransports = localTransports.length > 1;
  const hasChanges =
    JSON.stringify(initialTransportRef.current) !==
    JSON.stringify(localTransports);

  if (saveRef) saveRef.current = hasChanges ? handleSaveTransportChanges : null;

  useEffect(() => {
    onUnsavedEdit(hasChanges);
  }, [onUnsavedEdit, hasChanges]);

  // The shift's effective work time — accounts for any in-flight worktime
  // edit in the slice, then any server-confirmed manual edit, then the
  // employee's base shift. Used both for the dropdown bounds and as the
  // default end-anchor for newly added segments.
  const workTimeForValidation =
    manualEdits[employeeShiftId]?.employeeWorkTime ??
    manualShiftChanges?.employeeWorkTime ??
    employeeWorkTime;

  const workTimeStartHHMM = workTimeForValidation.workTimeStart
    ? timestampService.getHoursAndMinutesTimestamp(
        workTimeForValidation.workTimeStart,
      )
    : undefined;
  const workTimeEndHHMM = workTimeForValidation.workTimeEnd
    ? timestampService.getHoursAndMinutesTimestamp(
        workTimeForValidation.workTimeEnd,
      )
    : undefined;

  // Invariant: when the array has at least one segment, the LAST entry fills
  // available shift time — its start picks up from the previous segment's
  // end (or the shift start when it's the only one left), and its end is
  // anchored at the shift end. Applied on add (so new segments default to
  // sensible bounds instead of "00:00") and on delete (so the remainder
  // absorbs any gap left behind without the operator clicking twice to
  // re-pass validation).
  const enforceLastSegmentFillsShift = (
    segments: EmployeeTransport[],
  ): EmployeeTransport[] => {
    if (segments.length === 0) return segments;
    if (!workTimeStartHHMM || !workTimeEndHHMM) return segments;
    const lastIndex = segments.length - 1;
    const previousEnd = segments[lastIndex - 1]?.end ?? workTimeStartHHMM;
    return segments.map((segment, index) =>
      index === lastIndex
        ? { ...segment, start: previousEnd, end: workTimeEndHHMM }
        : segment,
    );
  };

  const addTransport = () => {
    if (!workTimeStartHHMM || !workTimeEndHHMM) return;
    if (isFullyCovered) {
      setIsAddBlocked(true);
      return;
    }
    setIsAddBlocked(false);
    setLocalTransports((previousTransports) =>
      enforceLastSegmentFillsShift([
        ...previousTransports,
        {
          transportationType: "",
          start: workTimeStartHHMM,
          end: workTimeEndHHMM,
        },
      ]),
    );
  };

  const deleteTransport = (transportIndex: number) => {
    if (!hasMultipleTransports) return;
    setIsAddBlocked(false);
    setLocalTransports((transports) =>
      enforceLastSegmentFillsShift(
        transports.filter((_, index) => index !== transportIndex),
      ),
    );
  };

  const updateTransport = (
    transportIndex: number,
    updates: {
      transportationType?: TransportationType;
      start?: string;
      end?: string;
    },
  ) => {
    setLocalTransports((transports) =>
      transports.map((transport, index) =>
        index === transportIndex
          ? {
              transportationType:
                updates.transportationType ?? transport.transportationType,
              start: updates.start ?? transport.start,
              end: updates.end ?? transport.end,
            }
          : transport,
      ),
    );
  };

  const hasEmptyFields = localTransports.some((transport) => {
    return !transport.transportationType || !transport.start || !transport.end;
  });

  const shiftStartTimeMinutes = workTimeForValidation.workTimeStart
    ? timestampService.timeToMinutesSum(workTimeForValidation.workTimeStart)
    : 0;
  const shiftEndTimeMinutes = workTimeForValidation.workTimeEnd
    ? timestampService.timeToMinutesSum(workTimeForValidation.workTimeEnd)
    : 0;

  const localTimeIntervals = localTransports.map((transport) => ({
    startMinutes: timestampService.timeToMinutesSum(transport.start),
    endMinutes: timestampService.timeToMinutesSum(transport.end),
  }));

  const isOutsideWorkHours =
    !hasEmptyFields &&
    TransportValidationUtil.hasOutsideWorkHours(
      localTimeIntervals,
      shiftStartTimeMinutes,
      shiftEndTimeMinutes,
    );

  const hasOverlap =
    !hasEmptyFields && TransportValidationUtil.hasOverlap(localTimeIntervals);

  const totalTransportMinutes = !hasEmptyFields
    ? TransportValidationUtil.getTotalMinutes(localTimeIntervals)
    : 0;

  const worktimeMinutes = TransportValidationUtil.getWorktimeMinutes(
    shiftStartTimeMinutes,
    shiftEndTimeMinutes,
  );

  const isFullyCovered =
    !hasEmptyFields &&
    !isOutsideWorkHours &&
    !hasOverlap &&
    worktimeMinutes > 0 &&
    totalTransportMinutes >= worktimeMinutes;

  const hasWorktimeGap =
    !hasEmptyFields &&
    !isOutsideWorkHours &&
    !hasOverlap &&
    totalTransportMinutes < worktimeMinutes;

  let transportErrorMessage = null;
  if (hasEmptyFields) {
    transportErrorMessage = labels.errorEmptyFields;
  } else if (isOutsideWorkHours) {
    transportErrorMessage = labels.errorOutsideWorkHours;
  } else if (hasOverlap) {
    transportErrorMessage = labels.errorOverlappingTimeWindow;
  } else if (hasWorktimeGap) {
    transportErrorMessage = labels.errorNotFilledWorkHours;
  }

  // For a given segment index, return the times that fall inside any *other*
  // segment. The start dropdown excludes [other.start, other.end) so a new
  // segment can sit back-to-back at another's end but not begin where another
  // already begins; the end dropdown is the mirror image.
  const stepMinutes = 15;
  const minutesToHHMM = (totalMinutes: number) => {
    const hh = Math.floor(totalMinutes / 60);
    const mm = totalMinutes % 60;
    return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  };

  const [showSuccessReminder, setShowSuccessReminder] =
    useState<boolean>(false);
  const computeExcludedTimes = (
    currentIndex: number,
    side: "start" | "end",
  ): string[] => {
    const excluded = new Set<string>();
    localTransports.forEach((segment, index) => {
      if (index === currentIndex) return;
      const segStartMin = timestampService.timeToMinutesSum(segment.start);
      const segEndMin = timestampService.timeToMinutesSum(segment.end);
      const rangeStart =
        side === "start" ? segStartMin : segStartMin + stepMinutes;
      const rangeEnd = side === "start" ? segEndMin : segEndMin + stepMinutes;
      for (let m = rangeStart; m < rangeEnd; m += stepMinutes) {
        excluded.add(minutesToHHMM(m));
      }
    });
    return [...excluded];
  };

  async function handleSaveTransportChanges() {
    const workTimeToUse =
      manualEdits[employeeShiftId]?.employeeWorkTime ??
      manualShiftChanges?.employeeWorkTime ??
      employeeWorkTime;
    if (!workTimeToUse?.workTimeStart || !workTimeToUse?.workTimeEnd) return;

    const updatedTransports: EmployeeTransport[] = localTransports.map(
      (transport) => ({
        transportationType: transport.transportationType,
        start: timestampService.mergeTimeIntoDate(
          workTimeToUse.workTimeStart!,
          transport.start,
        ),
        end: timestampService.mergeTimeIntoDate(
          workTimeToUse.workTimeEnd!,
          transport.end,
        ),
      }),
    );

    setIsPending(true);
    try {
      await EmployeeSettingsService.editTransportSegments(
        employeeShiftId,
        updatedTransports,
        dispatch,
      );
      setShowSuccessReminder(true);
    } catch {
      setLocalTransports(initialTransportRef.current);
      setIsPending(false);
      return;
    }
    dispatch(
      editEmployeeTransportType({
        shiftId: employeeShiftId,
        transportationType: updatedTransports,
      }),
    );
    initialTransportRef.current = localTransports;

    // Transport segments drive per-task transport durations on the backend,
    // so refresh the visible Gantt to pick up the recomputed timings.
    if (
      queryKey.filterId != null &&
      queryKey.date != null &&
      queryKey.solutionType != null
    ) {
      await dispatch(
        getGanttChartData({
          filterId: queryKey.filterId,
          date: queryKey.date,
          solutionType: queryKey.solutionType,
        }),
      );
    }
    setIsPending(false);
  }

  const handleDiscardTransportChanges = () => {
    setLocalTransports(initialTransportRef.current);
  };

  return (
    <LabeledSection
      title={labels.transport}
      titleSize="h4"
      spacing="small"
      extraOptions={
        <StandardButton
          ariaLabel={labels.addTransportType}
          variant="TERTIARY"
          onClick={addTransport}
          classes="!h-[17.5px]"
        >
          {labels.addTransportType}
        </StandardButton>
      }
    >
      {localTransports.map((transport, index) => (
        <EmployeeTransportListItem
          key={`${transport.transportationType}-${index}`}
          transport={transport}
          options={options}
          onTypeChange={(newOption) =>
            updateTransport(index, { transportationType: newOption })
          }
          onStartChange={(newStart) =>
            updateTransport(index, { start: newStart })
          }
          onEndChange={(newEnd) => updateTransport(index, { end: newEnd })}
          canDelete={hasMultipleTransports}
          onDelete={() => deleteTransport(index)}
          minTime={workTimeStartHHMM}
          maxTime={workTimeEndHHMM}
          excludedStartTimes={computeExcludedTimes(index, "start")}
          excludedEndTimes={computeExcludedTimes(index, "end")}
          typeError={!transport.transportationType}
          startError={!transport.start}
          endError={!transport.end}
        />
      ))}

      {isAddBlocked && isFullyCovered && (
        <p className="flex justify-end text-[var(--col-error-4)]">
          {labels.errorTransportCoversFullWorkTime}
        </p>
      )}

      {transportErrorMessage && (
        <p className="flex justify-end text-[var(--col-error-4)]">
          {transportErrorMessage}
        </p>
      )}

      {showSuccessReminder && hasMultipleTransports && (
        <div className={drawerStles.purpleInfoBox}>
          {labels.rememberToCreateOperationalTask}
        </div>
      )}

      {hasChanges && (
        <div className="flex gap-[var(--spacing-sm)] w-full justify-end">
          <StandardButton
            ariaLabel={labels.discard}
            variant="SECONDARY"
            classes="w-fit"
            onClick={handleDiscardTransportChanges}
          >
            {labels.discard}
          </StandardButton>

          <StandardButton
            ariaLabel={labels.save}
            variant="PRIMARY"
            classes="w-fit"
            onClick={handleSaveTransportChanges}
            disable={!!transportErrorMessage}
            pending={isPending}
          >
            {labels.save}
          </StandardButton>
        </div>
      )}
    </LabeledSection>
  );
};

export default EmployeeTransportType;
